<template>
  <v-col v-show="visible" v-bind="$attrs">
    <VglDefs v-if="isGraphVisible">
      <template #averagedPoses>
        <VglLine v-for="(bone, index) in bones" :key="`bone-${index}`">
          <template #geometry>
            <VglGeometry>
              <template #position>
                <VglFloat32Attribute
                  :array="bone"
                  :item-size="3"
                />
              </template>
            </VglGeometry>
          </template>
          <template #material>
            <VglLineBasicMaterial
              :linewidth="2"
              color="#546E7A"
            />
          </template>
        </VglLine>
        <VglMesh 
          v-for="(pose, index) in averagedPoses" 
          :key="`pose-${index}`"
          :position-x="pose.x"
          :position-y="-pose.y" 
          :position-z="-pose.z"
        >
          <template #geometry>
            <VglSphereGeometry 
              :radius="0.01"
            />
          </template>
          <template #material>
            <VglMeshStandardMaterial
              color="#546E7A"
            />
          </template>
        </VglMesh>
      </template>

      <template #currentPoses>
        <VglLine v-for="(bone, index) in currentBones" :key="`current-bone-${index}`">
          <template #geometry>
            <VglGeometry>
              <template #position>
                <VglFloat32Attribute
                  :array="bone"
                  :item-size="3"
                />
              </template>
            </VglGeometry>
          </template>
          <template #material>
            <VglLineBasicMaterial
              :linewidth="2"
              color="#8888CC"
            />
          </template>
        </VglLine>
        <VglMesh 
          v-for="(pose, index) in currentPoses" 
          :key="`current-pose-${index}`"
          :position-x="pose.x"
          :position-y="-pose.y" 
          :position-z="-pose.z"
        >
          <template #geometry>
            <VglSphereGeometry 
              :radius="0.01"
            />
          </template>
          <template #material>
            <VglMeshStandardMaterial
              color="#8888CC"
            />
          </template>
        </VglMesh>
      </template>
    </VglDefs>

    <v-row 
      v-if="noMenu == null"
      class="d-flex align-center"
    >
      <v-chip
        class="ml-3 my-0"
        :color="color"
        outlined
      >
        {{ title }}
      </v-chip>

      <v-btn 
        @click="refreshCamera" 
        icon 
        :color="color"
        class="ml-2"
        v-tooltip="'視点初期化'"
      >
        <v-icon>mdi-eye-refresh</v-icon>
      </v-btn>

      <template v-if="plane == null">
        <v-btn 
          @click="setCameraAngle(0, 0, 0)" 
          icon 
          :color="color"
          active
          class="ml-2"
          v-tooltip="'側面'"
        >
          <v-icon>mdi-axis-x-arrow</v-icon>
        </v-btn>

        <v-btn 
          @click="setCameraAngle(0, 90, 0)"
          icon 
          :color="color"
          class="ml-2"
          v-tooltip="'背面'"
        >
          <v-icon>mdi-axis-y-arrow</v-icon>
        </v-btn>

        <v-btn 
          @click="setCameraAngle(90, 0, 0)" 
          icon
          :color="color"
          class="ml-2"
          v-tooltip="'上面'"
        >
          <v-icon>mdi-axis-z-arrow</v-icon>
        </v-btn>

        <v-btn 
          @click="isPerspective=!isPerspective" 
          icon 
          :color="color"
          class="ml-2"
          v-tooltip="'透視投影/平行投影'"
        >
          <svg v-if="isPerspective" style="width:24px;height:24px" viewBox="0 0 24 24">
            <path fill="currentColor" d="M20,15.5C20,15.88 19.79,16.21 19.47,16.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L4.53,16.38C4.21,16.21 4,15.88 4,15.5L3,7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,4.18C11.59,4.06 11.79,4 12,4C12.21,4 12.41,4.06 12.57,4.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5L20,16.5M12,6L6.04,7.5L12,10.85L17.96,7.5M6,14.91L11,19.29V12.58L5,9.21M18,14.91L19,9.21L13,12.58V19.25Z" />
          </svg>
          <v-icon v-else>mdi-cube-outline</v-icon>
        </v-btn>
      </template>

      <v-btn 
        @click="isGridVisible = !isGridVisible" 
        icon 
        :color="color" 
        class="ml-2"
        v-tooltip="gridCaption"
      >
        <v-icon>{{ isGridVisible ? 'mdi-grid' : 'mdi-grid-off' }}</v-icon>
      </v-btn>

      <v-btn 
        @click="zoomInGraph" 
        icon 
        :color="color" 
        class="ml-2"
      >
        <v-icon>mdi-magnify-plus</v-icon>
      </v-btn>
      
      <v-btn 
        @click="zoomOutGraph" 
        icon 
        :color="color" 
        class="ml-2"
      >
        <v-icon>mdi-magnify-minus</v-icon>
      </v-btn>
      <v-spacer />
      <v-switch
        class="pa-0 ma-0 ml-auto mr-5"
        dense
        v-model="isCurrentPoseVisible"
        :label="posture.isVideo ? '現在' : '選択'"
        color="primary"
        hide-details
      ></v-switch>

      <v-switch
        class="pa-0 ma-0 ml-auto mr-5"
        dense
        v-model="isAveragedPoseVisible"
        label="平均"
        color="gray"
        hide-details
      ></v-switch>
    </v-row>
    <v-row>
      <v-col class="pt-1">
        <div
          class="background"
          style="position: relative;"
          :style="{
            height: `${containerHeight}px`
          }"
          @mousedown.prevent="startDragging"
          @mousemove.prevent="rotateGraph"
          @wheel.ctrl.prevent="scaleGraph"
          ref="container"
          v-resize="resizeContainer"
          v-observe-visibility="isVisible => (isVisible ? resizeContainer() : false)"
          v-tooltip="{
            content: `ドラッグで${isRotatable ? '回転' : '移動'}<br/>ctrl+wheel、またはマウスオーバしてctrl+「+」/ctrl+「-」で拡大/縮小`,
            delay: {
              show: 2000,
              hide: 0
            },
          }"
          @mouseenter="addKeyEventListeners"
          @mouseleave="removeKeyEventListeners"
        >
          <VglDefs ref="defs" v-if="isGraphVisible">
            <template v-slot:[`grid${id}`]>
              <VglLine 
                v-for="(line, index) in subGridLines" 
                :key="`sub-grid-line-${index}`"
              >
                <template #geometry>
                  <VglGeometry>
                    <template #position>
                      <VglFloat32Attribute
                        :array="line"
                        :item-size="3"
                      />
                    </template>
                  </VglGeometry>
                </template>
                <template #material>
                  <VglLineBasicMaterial
                    :linewidth="2"
                    color="#DDDDDD"
                  />
                </template>
              </VglLine>
            </template>
          </VglDefs>
          <VglRenderer 
            v-if="isGraphVisible"
            style="width: 100%; height: 100%;"
            :alpha="true"
            :antialias="true"
            :preserve-drawing-buffer="true"
            ref="renderer"
            :id="`posture${id}`"
          >
            <template #scene>
              <VglScene ref="scene">
                <VglGroup
                  rotation="quaternion"
                  :scale-x="scaleX"
                  :scale-y="scaleY"
                  :scale-z="scaleZ"
                  :rotation-w="quaternion[0]"
                  :rotation-x="quaternion[1]"
                  :rotation-y="quaternion[2]"
                  :rotation-z="quaternion[3]"
                >
                  <VglUse 
                    v-if="isGridVisible" 
                    :href="`grid${id}`" 
                  />

                  <VglUse 
                    v-if="isAveragedPoseVisible"
                    href="averagedPoses"
                  />
                  <VglUse 
                    v-if="isCurrentPoseVisible" 
                    href="currentPoses"
                  />
                  <VglDirectionalLight 
                    :position-x="2" 
                    :position-y="1.5" 
                    :position-z="1"
                    :intensity="0.4"
                  />
                  <VglAmbientLight 
                    :intensity="0.4"
                  />
                </VglGroup>
              </VglScene>
            </template>
            <template #camera>
              <VglPerspectiveCamera 
                v-if="isPerspective"
                ref="camera"
                :position-z="cameraPosition" 
                :rotation="isRotatable ? 'lookAt' : null" 
              />
              <VglOrthographicCamera 
                v-else
                ref="camera"
                :left="-cameraPosition" 
                :right="cameraPosition" 
                :top="cameraPosition/aspectRatio" 
                :bottom="-cameraPosition/aspectRatio"
                :position-x="translationX"
                :position-y="translationY"
                :position-z="cameraPosition" 
                :rotation="isRotatable ? 'lookAt' : null"
              />
            </template>
          </VglRenderer>

          <v-overlay :value="loading || isLoading" absolute>
            <v-progress-circular
              indeterminate
              size="64"
            ></v-progress-circular>
          </v-overlay>
        </div>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { calcQuaternionFromEulerAngles, calcQuaternionMultiplication } from '@js/utils/MatrixUtil.js';
import { Raycaster } from 'three';

import { 
  VglDefs,
  VglGroup,
  VglUse,
  VglRenderer, 
  VglScene, 
  VglPerspectiveCamera,
  VglOrthographicCamera,
  VglLine,
  VglLineBasicMaterial,
  VglGeometry,
  VglMesh, 
  VglMeshStandardMaterial, 
  VglSphereGeometry,
  VglFloat32Attribute,
  VglDirectionalLight, 
  VglAmbientLight,
} from 'vue-gl';
import { mapState } from 'vuex';

export default {
  components: {
    VglDefs,
    VglGroup,
    VglUse,
    VglRenderer,
    VglScene,
    VglPerspectiveCamera,
    VglOrthographicCamera,
    VglLine,
    VglLineBasicMaterial,
    VglGeometry,
    VglMesh, 
    VglMeshStandardMaterial, 
    VglSphereGeometry,
    VglFloat32Attribute,
    VglDirectionalLight, 
    VglAmbientLight,
  },
  props: {
    id: {},
    posture: {
      required: true,
    },
    loading: {
      default: false,
    },
    visible: {
      default: true,
    },
    color: {
      default: 'secondary'
    },
    title: {
    },
    verticalOffset: {
      default: 0,
    },
    horizontalOffset: {
      default: 0,
    },
    scaleX: {
      default: 1,
    },
    scaleY: {
      default: 1,
    },
    scaleZ: {
      default: 1,
    },

    plane: {
      defaut: null,
    },
    noMenu: {
      default: null,
    },
    perspective: {
      default: null,
    },
    lineOpacity: {
      default: 0.65,
    },
    gridCaption: {
      default: null,
    },
    initialRoll: {
      default: 25,
    },
    initialPitch: {
      default: 45,
    },
    initialYaw: {
      default: 0,
    },
    draggingMode: {
      default: 'rotation',
    },

    gridMode: {
      default: 'all', // top | side | front | all
    },
    initialCameraPosition: {
      default: 1.5,
    },
    maxCameraPosition: {
      default: 3.0,
    },
    minCameraPosition: {
      default: 0.5,
    },
    aspectRatio: {
      default: 1.92
    },

    averagedPoses: {
      default: () => [],
    },
    currentPoses: {
      default: () => [],
    },
  },
  data: function(){
    return {
      isPerspective: false,
      isGridVisible: true,
      isAveragedPoseVisible: true,
      isCurrentPoseVisible: true,

      cameraPosition: 1.0,
      stepWidth: 0.25,
      isDragging: false,
      quaternion: [1, 0, 0, 0],
      previousQuaternion: [1, 0, 0, 0],
      containerWidth: 0,
      translationX: 0,
      translationY: 0,
      gridInterval: 0.1,

      loadingCount: 0,

      raycaster: null,
    }
  },
  watch: {
    perspective: {
      handler(newVal){
        this.isPerspective = newVal != null;
      },
      immediate: true,
    },
    gaitId: function(){
      this.refreshCamera();
    },
    gridMode: function(){
      this.refreshCamera();
    },
  },
  computed: {
    ...mapState([
      'isGraphVisible',
    ]),
    containerHeight: function(){
      return parseInt(this.containerWidth/this.aspectRatio);
    },
    isLoading: function(){
      return this.loadingCount > 0;
    },
    isRotatable: function(){
      return this.draggingMode === 'rotation';
    },
    averagedPositions: function(){
      const rtn = {
        x: [],
        y: [],
        z: [],
      }

      this.averagedPoses.forEach(p => {
        rtn.x.push(p.x);
        rtn.y.push(-p.y);
        rtn.z.push(-p.z);
      })

      rtn.minX = Math.min(...rtn.x);
      rtn.minY = Math.min(...rtn.y);
      rtn.minZ = Math.min(...rtn.z);

      rtn.maxX = Math.max(...rtn.x);
      rtn.maxY = Math.max(...rtn.y);
      rtn.maxZ = Math.max(...rtn.z);

      return rtn;
    },
    maxGridX: function(){
      return this.averagedPositions.maxX + 0.5;
    },
    minGridX: function(){
      return this.averagedPositions.minX - 0.5;
    },
    maxGridY: function(){
      return this.averagedPositions.maxY + 0.2;
    },
    minGridY: function(){
      return this.averagedPositions.minY;
    },
    maxGridZ: function(){
      return this.averagedPositions.maxZ + 0.5;
    },
    minGridZ: function(){
      return this.averagedPositions.minZ - 0.5;
    },
    subGridLines: function(){
      let rtn = [];
      const interval = this.gridInterval;

      
      if(['top', 'all'].some(m => m === this.gridMode)){
        for(let i=this.minGridX/interval, iMax=this.maxGridX/interval; i<=iMax; i++){
          rtn.push([
            -this.horizontalOffset + interval*i, 
            -this.verticalOffset + this.minGridY, 
            this.minGridZ, 

            -this.horizontalOffset + interval*i, 
            -this.verticalOffset + this.minGridY, 
            this.maxGridZ,
          ]);
        }

        for(let i=this.minGridZ/interval, iMax=this.maxGridZ/interval; i<=iMax; i++){
          rtn.push([
            this.minGridX - this.horizontalOffset, 
            -this.verticalOffset + this.minGridY, 
            interval*i, 

            this.maxGridX - this.horizontalOffset, 
            -this.verticalOffset + this.minGridY, 
            interval*i,
          ]);
        }
      }

      if(['front', 'all'].some(m => m === this.gridMode)){
        for(let i=this.minGridY/interval, iMax=this.maxGridY/interval; i<=iMax; i++){
          rtn.push([
            this.minGridX - this.horizontalOffset, 
            -this.verticalOffset + interval*i, 
            this.minGridZ, 

            this.minGridX - this.horizontalOffset, 
            -this.verticalOffset + interval*i, 
            this.maxGridZ,
          ]);
        }

        for(let i=this.minGridZ/interval, iMax=this.maxGridZ/interval; i<=iMax; i++){
          rtn.push([
            this.minGridX - this.horizontalOffset, 
            -this.verticalOffset + this.minGridY, 
            interval*i, 

            this.minGridX - this.horizontalOffset, 
            -this.verticalOffset + this.maxGridY, 
            interval*i,
          ]);
        }
      }

      if(['side', 'all'].some(m => m === this.gridMode)){
        for(let i=this.minGridX/interval, iMax=this.maxGridX/interval; i<=iMax; i++){
          rtn.push([
            -this.horizontalOffset + interval*i, 
            -this.verticalOffset + this.minGridY, 
            this.minGridZ, 

            -this.horizontalOffset + interval*i, 
            -this.verticalOffset + this.maxGridY, 
            this.minGridZ,
          ]);
        }

        for(let i=this.minGridY/interval, iMax=this.maxGridY/interval; i<=iMax; i++){
          rtn.push([
            this.minGridX - this.horizontalOffset, 
            -this.verticalOffset + interval*i, 
            this.minGridZ, 
            
            this.maxGridX - this.horizontalOffset, 
            -this.verticalOffset + interval*i, 
            this.minGridZ,
          ]);
        }
      }

      return rtn;
    },
    bones: function(){
      return this.createBones(this.averagedPoses);
    },
    currentBones: function(){
      return this.createBones(this.currentPoses);
    },
  },
  beforeDestroy: function(){
  },
  mounted: function(){
    this.refreshCamera();
    this.$nextTick(() => {
      this.containerWidth = this.$refs.container.clientWidth;
    });

    this.raycaster = new Raycaster();
  },
  methods: {
    startDragging: function(){
      this.previousQuaternion = [...this.quaternion];
      this.dx = 0;
      this.dy = 0;

      this.isDragging = true;
      window.addEventListener('mouseup', this.endDragging);
    },
    rotateGraph: function(e){
      if(this.isDragging){
        if(this.isRotatable){
          this.dx += e.movementX;
          this.dy += e.movementY;

          const dx = this.dx/this.containerWidth;
          const dy = this.dy/this.containerHeight;
          const distance = Math.sqrt(dx*dx + dy*dy);
          if(distance != 0){
            const theta = distance*Math.PI;
            const sinTheta = Math.sin(theta)/distance;
            this.quaternion = calcQuaternionMultiplication(
              [
                Math.cos(theta), 
                dy*sinTheta,
                dx*sinTheta, 
                0.0
              ],
              this.previousQuaternion
            )
          }
        }else{
          this.translationX -= 2*this.cameraPosition*e.movementX/this.containerWidth;
          this.translationY += 2/this.aspectRatio*this.cameraPosition*e.movementY/this.containerHeight;
        }
      }
    },
    scaleGraph: function(e){
      let val = this.cameraPosition*(1.0 + e.deltaY*0.001);
      this.cameraPosition = Math.max(Math.min(this.maxCameraPosition, val), this.minCameraPosition);
    },
    endDragging: function(){
      window.removeEventListener('mouseup', this.endDragging);
      this.isDragging = false;
    },
    refreshCamera: function(){
      this.translationX = 0;
      this.translationY = 0;
      this.quaternion = calcQuaternionFromEulerAngles(
        this.initialRoll*Math.PI/180, 
        this.initialPitch*Math.PI/180, 
        this.initialYaw*Math.PI/180
      );
      this.cameraPosition = this.initialCameraPosition;
    },
    setCameraAngle: function(roll, pitch, yaw, cameraPosition=null){
      this.quaternion = calcQuaternionFromEulerAngles(
        roll*Math.PI/180, 
        pitch*Math.PI/180, 
        yaw*Math.PI/180
      );
      if(cameraPosition != null){
        this.cameraPosition = cameraPosition;
      }
    },
    resizeContainer: function(){
      this.$nextTick(() => {
        this.containerWidth = this.$refs.container.clientWidth;
      });
    },

    addKeyEventListeners: function(){
      window.addEventListener('keydown', this.executeKeyEvent);
    },
    removeKeyEventListeners: function(){
      this.highlighted = null;
      window.removeEventListener('keydown', this.executeKeyEvent);
    },
    zoomInGraph: function(){
      this.scaleGraph({deltaY: -100.0});
    },
    zoomOutGraph: function(){
      this.scaleGraph({deltaY: 100.0});
    },
    executeKeyEvent: function(e){
      if(e.ctrlKey){
        switch(e.key){
          case '+':
            e.preventDefault();
            this.zoomIn();
            break;
          case '-':
            e.preventDefault();
            this.zoomOut();
            break;
        }
      }
    },
    getBone: function(poses, from, to){
      return [
        poses[from].x, -poses[from].y, -poses[from].z, 
        poses[to].x, -poses[to].y, -poses[to].z, 
      ]
    },
    createBones: function(poses){
      let rtn = [];
      if(poses.length == 0){
        return rtn;
      }
      rtn.push(this.getBone(poses, 11, 12));
      rtn.push(this.getBone(poses, 11, 23));
      rtn.push(this.getBone(poses, 24, 23));
      rtn.push(this.getBone(poses, 24, 12));

      rtn.push(this.getBone(poses, 23, 25));
      rtn.push(this.getBone(poses, 25, 27));
      rtn.push(this.getBone(poses, 27, 29));
      rtn.push(this.getBone(poses, 27, 31));
      rtn.push(this.getBone(poses, 29, 31));

      rtn.push(this.getBone(poses, 24, 26));
      rtn.push(this.getBone(poses, 26, 28));
      rtn.push(this.getBone(poses, 28, 30));
      rtn.push(this.getBone(poses, 28, 32));
      rtn.push(this.getBone(poses, 30, 32));
      
      rtn.push(this.getBone(poses, 11, 13));
      rtn.push(this.getBone(poses, 13, 15));
      rtn.push(this.getBone(poses, 15, 21));
      rtn.push(this.getBone(poses, 15, 17));
      rtn.push(this.getBone(poses, 15, 19));
      rtn.push(this.getBone(poses, 17, 19));

      rtn.push(this.getBone(poses, 12, 14));
      rtn.push(this.getBone(poses, 16, 14));
      rtn.push(this.getBone(poses, 16, 22));
      rtn.push(this.getBone(poses, 16, 18));
      rtn.push(this.getBone(poses, 16, 20));
      rtn.push(this.getBone(poses, 18, 20));

      
      rtn.push(this.getBone(poses, 0, 4));
      rtn.push(this.getBone(poses, 5, 4));
      rtn.push(this.getBone(poses, 5, 6));
      rtn.push(this.getBone(poses, 8, 6));

      rtn.push(this.getBone(poses, 0, 1));
      rtn.push(this.getBone(poses, 2, 1));
      rtn.push(this.getBone(poses, 2, 3));
      rtn.push(this.getBone(poses, 7, 3));
      
      rtn.push(this.getBone(poses, 9, 10));
      return rtn;
    },
  },
}
</script>


<style lang="scss" scoped>
.background{
//  background: black;
  border: 1px solid black;
}
</style>